import { Table } from 'antd';
import { T } from 'react-translator-component';

import { formatDateTime, formatHours, formatKwh, formatMoney, sortByStartDesc, sumBy } from './ocpiHelpers';
import type { Navigate } from './OcpiPage';
import * as S from './styled';

import { OcpiCdr, OcpiPage } from '../../../types/responses/Ocpi';

interface Props {
  cdrs: OcpiPage<OcpiCdr>;
  nav: Navigate;
}

const CdrsTab = ({ cdrs, nav }: Props) => {
  const list = cdrs.list;
  const currency = list.find((c) => c.currency)?.currency || 'BRL';
  const avgHours = list.length ? sumBy(list, (c) => c.durationHours) / list.length : undefined;

  return (
    <>
      <S.Grid columns={4}>
        <S.Kpi type="button" disabled>
          <span className="label">{T('CDRs in the period')}</span>
          <span className="value">{list.length}</span>
        </S.Kpi>
        <S.Kpi type="button" disabled>
          <span className="label">{T('Energy')}</span>
          <span className="value">{formatKwh(sumBy(list, (c) => c.kwh))}</span>
        </S.Kpi>
        <S.Kpi type="button" disabled>
          <span className="label">{T('Total')}</span>
          <span className="value">{formatMoney(sumBy(list, (c) => c.income), currency)}</span>
        </S.Kpi>
        <S.Kpi type="button" disabled>
          <span className="label">{T('Average duration')}</span>
          <span className="value">{formatHours(avgHours)}</span>
        </S.Kpi>
      </S.Grid>
      {cdrs.total > list.length && (
        <S.Toolbar>
          <S.Muted>
            {T('Showing the first')} {list.length} {T('of')} {cdrs.total}.
          </S.Muted>
        </S.Toolbar>
      )}
      <S.Panel>
        <Table
          rowKey="transactionPk"
          dataSource={sortByStartDesc(list)}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          onRow={(record) => ({ onClick: () => nav.open({ kind: 'cdr', pk: record.transactionPk }) })}
          locale={{ emptyText: T('No CDRs in the period.') }}
          columns={[
            { title: 'CDR', dataIndex: 'transactionPk', render: (pk) => <S.Mono>#{pk}</S.Mono> },
            {
              title: T('Station'),
              render: (_, c) => (
                <>
                  <div>{c.cdrLocationOcpi?.chargeBoxId || '—'}</div>
                  <S.Muted>{[c.cdrLocationOcpi?.city, c.cdrLocationOcpi?.state].filter(Boolean).join(' / ')}</S.Muted>
                </>
              ),
            },
            {
              title: T('Connector'),
              render: (_, c) => [c.cdrLocationOcpi?.connectorId, c.cdrLocationOcpi?.connectorType].filter(Boolean).join(' · ') || '—',
            },
            {
              title: T('Charge period'),
              render: (_, c) => `${formatDateTime(c.startTimestamp)} → ${formatDateTime(c.stopTimestamp)}`,
            },
            { title: T('Duration'), dataIndex: 'durationHours', align: 'right', render: formatHours },
            { title: T('Energy'), dataIndex: 'kwh', align: 'right', render: formatKwh },
            {
              title: T('Total'),
              align: 'right',
              render: (_, c) => (
                <>
                  <div>{formatMoney(c.income, c.currency)}</div>
                  <S.Muted>{c.currency}</S.Muted>
                </>
              ),
            },
          ]}
        />
      </S.Panel>
    </>
  );
};

export default CdrsTab;
